<template>
  <v-row dense>
    <v-col cols="12" md="4">
      <v-text-field
        :value="value.region"
        @input="emit('region', $event)"
        label="Región"
        outlined dense
      ></v-text-field>
    </v-col>

    <v-col cols="12" md="4">
      <ValidationProvider name="Ciudad" rules="required" v-slot="{ errors }">
        <v-autocomplete
          v-if="!readonly"
          v-model="selectedCity"
          :items="cities"
          item-text="name"
          item-value="relbase_id"
          label="Ciudad"
          outlined dense
          :loading="loadingCities"
          :error-messages="errors"
          return-object
          @change="onCityChange"
          :search-input.sync="citySearch"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>{{ loadingCities ? 'Cargando...' : 'Sin resultados' }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
        <v-text-field
          v-else
          :value="value.city"
          label="Ciudad"
          outlined dense
          disabled
          :hint="'Sincronizado con Relbase'"
          persistent-hint
        ></v-text-field>
      </ValidationProvider>
    </v-col>

    <v-col cols="12" md="4">
      <ValidationProvider name="Comuna" rules="required" v-slot="{ errors }">
        <v-autocomplete
          v-if="!readonly"
          v-model="selectedCommune"
          :items="communes"
          item-text="name"
          item-value="relbase_id"
          label="Comuna"
          outlined dense
          :loading="loadingCommunes"
          :disabled="!selectedCity"
          :error-messages="errors"
          return-object
          @change="onCommuneChange"
          :search-input.sync="communeSearch"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>{{ loadingCommunes ? 'Cargando...' : (selectedCity ? 'Sin resultados' : 'Seleccione ciudad primero') }}</v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
        <v-text-field
          v-else
          :value="value.comune"
          label="Comuna"
          outlined dense
          disabled
          :hint="'Sincronizado con Relbase'"
          persistent-hint
        ></v-text-field>
      </ValidationProvider>
    </v-col>

    <v-col cols="12" md="6">
      <ValidationProvider name="Calle" rules="required" v-slot="{ errors }">
        <v-text-field
          :value="value.street"
          @input="emit('street', $event)"
          label="Calle"
          outlined dense
          :error-messages="errors"
        ></v-text-field>
      </ValidationProvider>
    </v-col>
    <v-col cols="12" md="3">
      <v-text-field
        :value="value.number"
        @input="emit('number', $event)"
        label="Número"
        outlined dense
      ></v-text-field>
    </v-col>
    <v-col cols="12" md="3">
      <v-text-field
        :value="value.suite"
        @input="emit('suite', $event)"
        label="Referencia"
        outlined dense
        placeholder="Piso, Depto"
      ></v-text-field>
    </v-col>
  </v-row>
</template>

<script>
import { fetchRelbaseCities, fetchRelbaseCommunes } from '@/api/relbase'
import notifications from '@/utils/notifications'

export default {
  name: 'AddressRelbaseInline',
  props: {
    value: {
      type: Object,
      default: () => ({
        region: '', city: '', comune: '', street: '', number: '', suite: '',
        relbase_city_id: null, relbase_commune_id: null
      })
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    cities: [],
    communes: [],
    loadingCities: false,
    loadingCommunes: false,
    selectedCity: null,
    selectedCommune: null,
    citySearch: '',
    communeSearch: '',
  }),
  async created() {
    await this.loadCities()
    // Restore selections if value already has relbase ids (e.g. prefilled by lookup)
    if (this.value.relbase_city_id) {
      this.selectedCity = this.cities.find(c => c.relbase_id === this.value.relbase_city_id) || null
      if (this.selectedCity) {
        await this.loadCommunes(this.selectedCity.relbase_id)
        if (this.value.relbase_commune_id) {
          this.selectedCommune = this.communes.find(c => c.relbase_id === this.value.relbase_commune_id) || null
        }
      }
    }
  },
  watch: {
    // When parent prefills relbase ids (e.g. from RUT lookup), update selectors
    'value.relbase_city_id': async function(newId) {
      if (!newId) { this.selectedCity = null; this.selectedCommune = null; return }
      if (!this.cities.length) await this.loadCities()
      this.selectedCity = this.cities.find(c => c.relbase_id === newId) || null
      if (this.selectedCity) {
        await this.loadCommunes(this.selectedCity.relbase_id)
        if (this.value.relbase_commune_id) {
          this.selectedCommune = this.communes.find(c => c.relbase_id === this.value.relbase_commune_id) || null
        }
      }
    }
  },
  methods: {
    async loadCities() {
      this.loadingCities = true
      try { this.cities = await fetchRelbaseCities() }
      catch (e) { notifications.showError('Error cargando ciudades') }
      finally { this.loadingCities = false }
    },
    async loadCommunes(cityRelbaseId) {
      this.loadingCommunes = true
      this.communes = []
      try { this.communes = await fetchRelbaseCommunes(cityRelbaseId) }
      catch (e) { notifications.showError('Error cargando comunas') }
      finally { this.loadingCommunes = false }
    },
    async onCityChange(city) {
      this.selectedCommune = null
      if (city && city.relbase_id) {
        await this.loadCommunes(city.relbase_id)
        this.$emit('input', {
          ...this.value,
          city: city.name,
          comune: '',
          relbase_city_id: city.relbase_id,
          relbase_commune_id: null
        })
      } else {
        this.communes = []
        this.$emit('input', { ...this.value, city: '', comune: '', relbase_city_id: null, relbase_commune_id: null })
      }
    },
    onCommuneChange(commune) {
      if (commune) {
        this.$emit('input', {
          ...this.value,
          comune: commune.name,
          relbase_commune_id: commune.relbase_id
        })
      }
    },
    emit(field, val) {
      this.$emit('input', { ...this.value, [field]: val })
    }
  }
}
</script>
